import * as Actions from '../actions/answerActions';
import Answer from '../../entities/answer';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type AnswerAction = { 
    type: string; 
    answer: Answer | null; 
    answerId: string | Answer; 
    partial: boolean; 
    lastFetch: number; 
    answers: Answer[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Answer> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Answer[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Answer> | null, firmId: string, userId: string, updates?: Answer[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'answer', 'answers', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Answer>} data
 * @param {Answer[]} [updates]
 * @returns {ArrayMap<Answer>}
 */
function updateStorage(data: ArrayMap<Answer>, updates?: Answer[]): ArrayMap<Answer> {
	if (logging) console.debug('⮂ answerReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Answer[]} [updates]
 * @returns {ArrayMap<Answer>}
 */
function updateStorageNoReturn(updates?: Answer[]): void {
	if (logging) console.debug('⮂ answerReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Answer[]} [data]
 * @returns {ArrayMap<Answer>}
 */
function createState(data?: Answer[] | ArrayMap<Answer>): ArrayMap<Answer> {
	if (!data) return ArrayMapRegistry.clone<Answer>('answer', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Answer>('answer', data.map(e => e));
    else return ArrayMapRegistry.clone<Answer>('answer', data);
}

function answers(state: ArrayMap<Answer>, action: AnswerAction): ArrayMap<Answer> {
	if (!state) state = createState();

	let st: ArrayMap<Answer>;

	switch (action.type) {
		// action.answer - the new value of answer with id action.id
		// action.answerId - id of the target, used if new value is null
		case Actions.ANSWER_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ answerReducer: FETCH_ONE_SUCCESS', action);

			if (action.answer === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.answerId)) {
				st = state.clone();
				st.set(action.answer);
				// state.clear();

				updateStorageNoReturn([action.answer]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.answers
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.answers
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.answers
			 !partial, !stateEmpty, fetchEmpty	-> D return answers in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge answers in state newer than those in action.answers
		 */
		case Actions.ANSWER_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.answers.length === 0;
			const fetched = action.answers.map(answer => new Answer({...answer, persisted: true}));

			if (logging) console.debug(`⮂ answerReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} answers with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ answerReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ answerReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Answer> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Answer[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(answer => {
					const current = st.get(answer.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && answer.status === 'D') {
							// Remove it from state
							toUpdate.push(answer);
							st.delete(answer);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (answer.addedOn > current.addedOn) {
								toUpdate.push(answer);
								st.set(answer);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (answer.status !== 'D') {
							toUpdate.push(answer);
							st.set(answer);
						}
						else if (logging) console.debug('⮂ answerReducer: partialMerge: skipping deleted answer not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ answerReducer: empty: answers', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ answerReducer: fullMerge: answers', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localAnswer => {
                    // If this answer has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localAnswer.persisted) {
                        const fetchedAnswer = st.get(localAnswer.id);
                        if (!fetchedAnswer || localAnswer.addedOn > fetchedAnswer.addedOn) st.set(localAnswer);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ANSWER_FETCH_ONE_FAILURE:
			console.error('ANSWER_FETCH_ONE_FAILURE', action.answerId, action.err);

			// No update to storage required
			return state;

		case Actions.ANSWER_FETCH_FAILURE:
			console.error('ANSWER_FETCH_FAILURE', action.answerId, action.err);

			// No update to storage required
			return state;

		case Actions.ANSWER_RESTORE: {
		    let answer = new Answer({...action.answer});
            st = state.clone();

            const existing = st.get(answer.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (answer.addedOn >= existing.addedOn) {
                    st.set(answer);
                    return updateStorage(st, [answer]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (answer.status === 'D') return state;

                st.set(answer);
                return updateStorage(st, [answer]);
            }
        }
            
		case Actions.ANSWER_SAVE: {
		    // console.debug('⮂ ANSWER_SAVE reducer', action.answer?.id);
		    
			// clone the answer
			let answer = new Answer({...action.answer});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new answer in the state
			// If this is a create, add to the queue
			const _answer = st.get(answer.id);

			if (!_answer) st.set(answer);
			else {
				if (answer.addedOn >= _answer.addedOn) st.set(answer);
			}

			return updateStorage(st, [answer]);
		}

		case Actions.ANSWER_SAVE_MANY: {
		    if (logging) console.debug('⮂ ANSWER_SAVE_MANY reducer', action.answers.map(answer => answer.id).join(','));
		    
			// clone the answers
			let toProcess = action.answers.map(answer => new Answer(answer));

			st = state.clone();
			// state.clear();

			const updates: Answer[] = [];

			toProcess.forEach(answer => {
				// Add or update the new answer in the state
				// If this is a create, add to the queue
				const existing = st.get(answer.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (answer.status !== 'D') {
						st.push(answer);
						updates.push(answer);
					}
					else if (logging) console.debug('⮂ ANSWER_SAVE_MANY: skipping deleted answer not in state');
				}
				else {
					if (answer.addedOn >= existing.addedOn) {
						st.set(answer);
						updates.push(answer);
					}
					else if (logging) console.debug('⮂ Skipping, existing answer had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ANSWER_REMOVE: {
			// clone the answer
			const answer = new Answer({...action.answer});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(answer);

			return updateStorage(st, [answer]);
		}

		case Actions.ANSWER_RESTORE_FAILURE:
		case Actions.ANSWER_SAVE_FAILURE: {
		    console.warn('ANSWER_SAVE_FAILURE reducer', action.answer?.id);
		    if (!action.answer?.id) {
				console.warn('ANSWER_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const answer = ensure(Answer, action.answer);
			if (!answer) {
				console.error('Could not find answer after call to save');
				return state;
			}

			st = state.clone();

			const _answerRef = state.get(answer.id);

			if (_answerRef && (_answerRef.addedOn === answer.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _answer = new Answer({..._answerRef});
				_answer.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_answer.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('answer', _answer).then(r => {
						if (r.isErr()) console.error(`⮂ answerReducer: failed to save answer with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ answerReducer: saved answer with fatalError info');
					}).catch(err => console.error(`⮂ answerReducer: failed to save answer with fatalError info: ${err.message}`));

				st.set(_answer);
			}

			return updateStorage(st);
		}

		case Actions.ANSWER_SAVE_MANY_FAILURE: {
		    console.warn('ANSWER_SAVE_MANY_FAILURE reducer', action.answers.map(answer => answer.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ANSWER_RESTORE_SUCCESS:
		case Actions.ANSWER_SAVE_SUCCESS: {
			if (!action.answer?.id) {
				console.warn('ANSWER_SAVE_SUCCESS reducer');
                return state;
			}

			const answerRef = state.get(action.answer?.id);
			if (!answerRef) {
				console.warn('Could not find answer after call to save');
                return state;
			}

			st = state.clone();

			if (action.answer.addedOn >= answerRef.addedOn) {
				st.set(action.answer);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const answer = new Answer({...answerRef, persisted: true});
				st.set(answer);
			}

			return updateStorage(st, [action.answer]);
		}

		case Actions.ANSWER_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ANSWER_SAVE_MANY_SUCCESS reducer', action.answers.map(answer => answer.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const answers: Answer[] = [];

			action.answers.forEach(e => {
				const answerRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!answerRef) console.warn('Could not find answer after call to save');
				else if (e.addedOn >= answerRef.addedOn) {
				    st.set(e);
				    answers.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const answer = new Answer({...answerRef, persisted: true});
					st.set(answer);
					answers.push(answer);
				}
			});

			return updateStorage(st, answers);
		}

		case Actions.ANSWER_REMOVE_FAILURE: {
		    console.warn('ANSWER_REMOVE_FAILURE action', action.answer?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ANSWER_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ANSWER_RESTORE_MANY: {
			if (logging) console.debug('⮂ ANSWER_RESTORE_MANY reducer', action.answers.map(answer => answer.id).join(','));

			const toProcess = action.answers.map(answer => new Answer({...answer}));
			st = state.clone();

			const updates: Answer[] = [];

			toProcess.forEach(answer => {
				const existing = st.get(answer.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (answer.addedOn >= existing.addedOn) {
						st.set(answer);
						updates.push(answer);
					}
					else if (logging) console.debug('⮂ ANSWER_RESTORE_MANY: skipping older version of answer');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (answer.status !== 'D') {
						st.set(answer);
						updates.push(answer);
					}
					else if (logging) console.debug('⮂ ANSWER_RESTORE_MANY: skipping deleted answer not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ANSWER_REMOVE_MANY: {
			if (logging) console.debug('⮂ ANSWER_REMOVE_MANY reducer', action.answers.map(answer => answer.id).join(','));

			const toProcess = action.answers.map(answer => new Answer({...answer}));
			st = state.clone();

			toProcess.forEach(answer => {
				st.delete(answer);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default answers;